import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { database, auth } from './firebase';
import { signInAnonymously } from 'firebase/auth';
import { ref, onValue, update, get } from 'firebase/database';
import { Scanner } from '@yudiel/react-qr-scanner';
import axios from 'axios';

function AdHocClockDisplay() {
  const [searchParams] = useSearchParams();
  
  // Check if we have clock params from URL (after QR scan redirect)
  const urlClockId = searchParams.get('clock');
  const urlKey = searchParams.get('key');
  
  // State
  const [mode, setMode] = useState(urlClockId ? 'display' : 'scan'); // 'scan' or 'display'
  const [clockId, setClockId] = useState(urlClockId);
  const [displayKey, setDisplayKey] = useState(urlKey);
  const [clockData, setClockData] = useState(null);
  const [error, setError] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const [isLinked, setIsLinked] = useState(false); // Track if we're linked
  const [cueScoreMatch, setCueScoreMatch] = useState(null); // Full CueScore match data
  
  // Code entry state
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');
  
  // Timer state
  const [remaining, setRemaining] = useState(45);
  const [isRunning, setIsRunning] = useState(false);
  
  // Refs
  const intervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const wakeLockRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastPollRef = useRef(0);
  const lastKnownScoreRef = useRef({ p1Frame: null, p2Frame: null });
  const prevIsRunningRef = useRef(false);
  const remainingRef = useRef(45); // authoritative countdown value, interval reads this not state
  const prevSwitchVersionRef = useRef(null); // detects deliberate resets from remote

  // Wake lock - only when in display mode and linked
  useEffect(() => {
    if (mode !== 'display' || !isLinked) return;
    
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('🔆 Wake lock acquired (display)');
        }
      } catch (err) {
        console.log('Wake lock error:', err);
      }
    };
    
    requestWakeLock();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [mode, isLinked]);

  // Handle QR scan result
  const handleScan = (result) => {
    if (result && result[0]?.rawValue) {
      const url = result[0].rawValue;
      console.log('📱 Scanned:', url);
      
      try {
        const parsedUrl = new URL(url);
        const scannedClockId = parsedUrl.searchParams.get('clock');
        const scannedKey = parsedUrl.searchParams.get('key');
        
        if (scannedClockId && scannedKey) {
          setClockId(scannedClockId);
          setDisplayKey(scannedKey);
          setMode('display');
        } else {
          setError('Invalid QR code');
        }
      } catch (e) {
        setError('Invalid QR code format');
      }
    }
  };

  // Handle manual code entry
  const handleCodeSubmit = async () => {
    if (!codeInput.trim()) {
      setCodeError('Please enter a code');
      return;
    }
    
    // Remove any dashes and convert to uppercase
    const code = codeInput.trim().replace(/-/g, '').toUpperCase();
    console.log('🔑 Looking up code:', code);
    
    try {
      // Search for clock with matching code
      const clocksRef = ref(database, 'adHocClocks');
      const snapshot = await get(clocksRef);
      
      if (snapshot.exists()) {
        const clocks = snapshot.val();
        let foundClock = null;
        let foundKey = null;
        
        for (const [id, data] of Object.entries(clocks)) {
          // Skip leading dash, get next 6 characters (case insensitive)
          const idCode = id.replace(/^-/, '').substring(0, 6).toUpperCase();
          console.log('  Checking:', id, '-> code:', idCode);
          
          if (idCode === code.substring(0, 6) && data.status === 'active') {
            foundClock = id;
            foundKey = data.displayKey;
            console.log('  ✅ Match found!');
            break;
          }
        }
        
        if (foundClock && foundKey) {
          console.log('✅ Found clock:', foundClock);
          setClockId(foundClock);
          setDisplayKey(foundKey);
          setMode('display');
          setCodeError('');
        } else {
          setCodeError('Clock not found. Check the code and try again.');
        }
      } else {
        setCodeError('No active clocks found.');
      }
    } catch (err) {
      console.error('Code lookup error:', err);
      setCodeError('Error looking up code. Try again.');
    }
  };

  // Connect to clock and listen for updates
  useEffect(() => {
    if (!clockId || mode !== 'display') return;

    if (!auth.currentUser) {
      signInAnonymously(auth).catch(err => console.error('Anonymous sign-in failed:', err));
    }

    console.log('🔗 Connecting to clock:', clockId, 'with key:', displayKey);
    
    const clockRef = ref(database, `adHocClocks/${clockId}`);
    
    const unsubscribe = onValue(clockRef, async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        console.log('📡 Clock data received:', data);
        console.log('   displayKey from data:', data.displayKey);
        console.log('   displayKey in state:', displayKey);
        
        // Verify key - but skip check if displayKey matches OR if we're still syncing state
        // (displayKey might not be set yet if we just did code entry)
        if (displayKey && data.displayKey !== displayKey) {
          console.log('❌ Invalid display key - expected:', displayKey, 'got:', data.displayKey);
          setError('Invalid display key');
          return;
        }
        
        // If we connected via code entry and don't have displayKey yet, capture it
        if (!displayKey && data.displayKey) {
          console.log('📝 Capturing displayKey from data:', data.displayKey);
          setDisplayKey(data.displayKey);
        }
        
        // Check if ended
        if (data.status === 'ended') {
          console.log('🏁 Clock session ended');
          setError('Clock session has ended');
          setClockData(null);
          setIsLinked(false);
          return;
        }
        
        // Link this display if not already linked
        if (!data.displayLinked) {
          const deviceId = getDeviceId();
          console.log('🔗 Linking display:', deviceId);
          await update(ref(database, `adHocClocks/${clockId}`), {
            displayLinked: true,
            displayId: deviceId
          });
        }
        
        setIsLinked(true);
        setClockData(data);
        setIsRunning(data.isRunning);
        setError(null);

        // Snap remaining from Firebase only when clock transitions from stopped to running
        // (e.g. remote presses START or SWITCH). While running, local interval owns the countdown.
        const wasRunning = prevIsRunningRef.current;
        const nowRunning = data.isRunning;
        const switchChanged = data.switchVersion !== prevSwitchVersionRef.current
          && prevSwitchVersionRef.current !== null;

        if (switchChanged) {
          // Remote pressed SWITCH - reset to full duration immediately
          remainingRef.current = data.duration;
          setRemaining(data.duration);
          setIsRunning(true);
          // Write the reset remaining back to Firebase so remote display stays in sync
          update(ref(database, `adHocClocks/${clockId}`), { remaining: data.duration });
        } else if (!wasRunning && nowRunning) {
          // Clock just started - snap to Firebase value so display and remote are in sync
          remainingRef.current = data.remaining;
          setRemaining(data.remaining);
        } else if (!nowRunning) {
          // Clock is stopped - always accept Firebase value (pause, reset, extension from remote)
          remainingRef.current = data.remaining;
          setRemaining(data.remaining);
        } else if (data.remaining > remainingRef.current + 2) {
          // Clock is running but remote pushed a significantly higher value
          // (reset or extension from remote) - snap to it
          remainingRef.current = data.remaining;
          setRemaining(data.remaining);
        }
        prevIsRunningRef.current = data.isRunning;
        prevSwitchVersionRef.current = data.switchVersion ?? null;
        
        // Seed the score ref on first load so the poll baseline is correct
        if (lastKnownScoreRef.current.p1Frame === null) {
          lastKnownScoreRef.current = {
            p1Frame: data.p1Frame ?? 0,
            p2Frame: data.p2Frame ?? 0
          };
        }
      } else {
        console.log('❌ Clock not found');
        setError('Clock session not found');
      }
    });
    
    return () => unsubscribe();
  }, [clockId, displayKey, mode]);

  // Timer countdown - ONLY when linked and running
  useEffect(() => {
    // Guard: Only run timer when linked, running, and has time remaining
    if (!isLinked || !isRunning || remaining <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    
    intervalRef.current = setInterval(() => {
      const newVal = remainingRef.current - 1;
      remainingRef.current = newVal;
      setRemaining(newVal);
      
      // Play beeps - ONLY when linked
      if (newVal <= 10 && newVal >= 0 && isLinked) {
        playBeep(newVal === 0 ? 'final' : 'warning');
      }
    }, 1000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLinked, isRunning, clockId]);

  // CueScore polling for score updates - ONLY when linked
  useEffect(() => {
    if (!clockData || clockData.status !== 'active' || !isLinked) {
      console.log('⏸️ Polling skipped - linked:', isLinked, 'status:', clockData?.status);
      return;
    }
    
    const pollCueScore = async () => {
      if (document.hidden) {
        console.log('⏸️ Polling skipped - tab hidden');
        return;
      }
      
      const now = Date.now();
      if (now - lastPollRef.current < 5000) return;
      lastPollRef.current = now;
      
      console.log('🔄 Polling CueScore - tournament:', clockData.tournamentId, 'match:', clockData.matchId);
      
      try {
        const response = await axios.get(
          `https://api.cuescore.com/tournament/?id=${clockData.tournamentId}`
        );
        
        console.log('📡 CueScore response received, matches:', response.data.matches?.length);
        
        const match = response.data.matches?.find(
          m => m.matchId === clockData.matchId || String(m.matchId) === String(clockData.matchId)
        );
        
        if (match) {
          const p1Frames = match.scoreA ?? 0;
          const p2Frames = match.scoreB ?? 0;
          
          // Store full match data for player names / lag winner
          setCueScoreMatch(match);

          // Compare against ref, not stale clockData closure
          const lastScore = lastKnownScoreRef.current;
          const scoreChanged = p1Frames !== lastScore.p1Frame || p2Frames !== lastScore.p2Frame;
          
          console.log('🎯 Match found - CueScore:', p1Frames, '-', p2Frames, '| Last known:', lastScore.p1Frame, '-', lastScore.p2Frame);
          
          if (scoreChanged) {
            const wasNewFrame = lastScore.p1Frame !== null &&
              (p1Frames + p2Frames) > (lastScore.p1Frame + lastScore.p2Frame);
            
            console.log('✅ Score changed! Updating Firebase...');
            
            // Update ref immediately so next poll won't re-trigger
            lastKnownScoreRef.current = { p1Frame: p1Frames, p2Frame: p2Frames };
            
            const updates = {
              p1Frame: p1Frames,
              p2Frame: p2Frames,
              lastPoll: now
            };
            
            if (wasNewFrame) {
              updates.p1ExtensionUsed = false;
              updates.p2ExtensionUsed = false;
              // Determine who breaks next: alternating from lag winner
              const newTotalFrames = p1Frames + p2Frames;
              const lagWinnerIsA = match.lagWinner === 'A';
              const p1BreaksNext = newTotalFrames % 2 === 0 ? lagWinnerIsA : !lagWinnerIsA;
              updates.p1Active = p1BreaksNext;
              console.log('🎱 New frame - breaking player:', p1BreaksNext ? match.playerA?.name : match.playerB?.name);
            }
            
            await update(ref(database, `adHocClocks/${clockId}`), updates);
            console.log('✅ Firebase updated');
          } else {
            console.log('ℹ️ No score change');
          }
          
          if (match.matchstatus === 'finished' || match.matchstatusCode === 2) {
            console.log('🏁 Match finished in CueScore');
            await update(ref(database, `adHocClocks/${clockId}`), {
              status: 'ended',
              isRunning: false
            });
          }
        } else {
          console.log('⚠️ Match not found in CueScore response. Looking for matchId:', clockData.matchId);
          console.log('   Available matches:', response.data.matches?.map(m => m.matchId));
        }
      } catch (err) {
        console.error('❌ CueScore poll error:', err);
      }
    };
    
    // Initial poll
    pollCueScore();
    
    // Poll every 5 seconds
    pollIntervalRef.current = setInterval(pollCueScore, 5000);
    
    const handleVisibility = () => {
      if (!document.hidden) pollCueScore();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [clockData?.tournamentId, clockData?.matchId, clockData?.status, clockId, isLinked]);

  // Helper functions
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('adhoc_display_id');
    if (!deviceId) {
      deviceId = 'display_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('adhoc_display_id', deviceId);
    }
    return deviceId;
  };

  const playBeep = (type) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'square';
      oscillator.frequency.value = type === 'final' ? 880 : 660;
      gainNode.gain.value = 1.0;
      
      oscillator.start();
      oscillator.stop(ctx.currentTime + (type === 'final' ? 0.5 : 0.15));
    } catch (err) {
      console.error('Audio error:', err);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (remaining <= 5) return '#ef4444';
    if (remaining <= 10) return '#f59e0b';
    return '#10b981';
  };

  // Emergency control functions
  const handleSwitch = async () => {
    const newP1Active = !clockData.p1Active;
    await update(ref(database, `adHocClocks/${clockId}`), {
      p1Active: newP1Active,
      remaining: clockData.duration,
      isRunning: true
    });
  };

  const handleReset = async () => {
    await update(ref(database, `adHocClocks/${clockId}`), {
      remaining: clockData.duration,
      isRunning: true
    });
  };

  const handlePauseResume = async () => {
    await update(ref(database, `adHocClocks/${clockId}`), {
      isRunning: !isRunning,
      remaining: remaining
    });
  };

  const handleExtension = async () => {
    const player = clockData.p1Active ? 'p1' : 'p2';
    const extensionField = player === 'p1' ? 'p1ExtensionUsed' : 'p2ExtensionUsed';
    const alreadyUsed = player === 'p1' ? clockData.p1ExtensionUsed : clockData.p2ExtensionUsed;
    
    if (alreadyUsed) return;
    
    const newRemaining = remainingRef.current + clockData.extensionDuration;
    remainingRef.current = newRemaining;
    setRemaining(newRemaining);
    await update(ref(database, `adHocClocks/${clockId}`), {
      remaining: newRemaining,
      [extensionField]: true
    });
  };

  // SCAN MODE
  if (mode === 'scan') {
    return (
      <div style={styles.container}>
        <div style={styles.scanHeader}>
          <h1 style={styles.scanTitle}>📺 Ad-Hoc Clock Display</h1>
          <p style={styles.scanSubtitle}>Scan QR code from organiser's phone</p>
        </div>
        
        {!showCodeEntry ? (
          <>
            <div style={styles.scannerWrapper}>
              <Scanner
                onScan={handleScan}
                onError={(err) => console.error('Scanner error:', err)}
                styles={{
                  container: { width: '100%', maxWidth: '400px' },
                  video: { borderRadius: '16px' }
                }}
              />
            </div>
            
            {error && (
              <div style={styles.errorBox}>
                {error}
              </div>
            )}
            
            <p style={styles.scanHint}>
              Position the QR code within the frame
            </p>
            
            <button 
              onClick={() => setShowCodeEntry(true)}
              style={styles.codeEntryToggle}
            >
              Or enter code manually
            </button>
          </>
        ) : (
          <div style={styles.codeEntrySection}>
            <p style={styles.codeEntryLabel}>Enter the 6-character code from the remote</p>
            
            <input
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              placeholder="e.g. ABC123"
              maxLength={10}
              style={styles.codeInput}
              autoFocus
            />
            
            {codeError && (
              <p style={styles.codeErrorText}>{codeError}</p>
            )}
            
            <div style={styles.codeButtons}>
              <button onClick={handleCodeSubmit} style={styles.codeSubmitButton}>
                Connect
              </button>
              <button 
                onClick={() => {
                  setShowCodeEntry(false);
                  setCodeInput('');
                  setCodeError('');
                }}
                style={styles.codeCancelButton}
              >
                Back to Scanner
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ERROR STATE
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorState}>
          <p style={{ fontSize: '24px', marginBottom: '16px' }}>❌</p>
          <p style={{ fontSize: '20px', marginBottom: '16px' }}>{error}</p>
          <button 
            onClick={() => {
              setMode('scan');
              setClockId(null);
              setDisplayKey(null);
              setError(null);
              setIsLinked(false);
            }}
            style={styles.retryButton}
          >
            Scan Again
          </button>
        </div>
      </div>
    );
  }

  // LOADING STATE
  if (!clockData || !isLinked) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>
          <p>Connecting to clock...</p>
        </div>
      </div>
    );
  }

  // DISPLAY MODE
  const activePlayer = clockData.p1Active ? 'p1' : 'p2';
  const extensionUsed = activePlayer === 'p1' ? !!clockData.p1ExtensionUsed : !!clockData.p2ExtensionUsed;

  return (
    <div style={styles.container}>
      {/* Tournament/Match Info */}
      <div style={styles.header}>
        <div style={styles.tournamentName}>{clockData.tournamentName}</div>
        {clockData.tableNumber && (
          <div style={styles.tableBadge}>Table {clockData.tableNumber}</div>
        )}
      </div>

      {/* Players and Score */}
      <div style={styles.playersSection}>
        {(() => {
          // p1Active in Firebase is the source of truth.
          // CueScore writes the correct breaking player to p1Active on each new frame.
          // The organiser can override freely via SWITCH between score changes.
          const p1Active = clockData.p1Active;
          const p1Name = cueScoreMatch?.playerA?.name || clockData.p1Name;
          const p2Name = cueScoreMatch?.playerB?.name || clockData.p2Name;

          return (
            <>
              <div style={{
                ...styles.playerCard,
                ...(p1Active && styles.activePlayer)
              }}>
                <div style={styles.playerName}>{p1Name}</div>
                <div style={styles.runOuts}>
                  {Array(clockData.p1RunOuts || 0).fill().map((_, i) => (
                    <span key={i} style={styles.rooBadge}>RO</span>
                  ))}
                </div>
                {p1Active && <div style={styles.activeIndicator}>◄ PLAYING</div>}
              </div>

              <div style={styles.scoreSection}>
                <div style={styles.score}>
                  {clockData.p1Frame} - {clockData.p2Frame}
                </div>
                <div style={styles.scoreLabel}>from CueScore</div>
              </div>

              <div style={{
                ...styles.playerCard,
                ...(!p1Active && styles.activePlayer)
              }}>
                <div style={styles.playerName}>{p2Name}</div>
                <div style={styles.runOuts}>
                  {Array(clockData.p2RunOuts || 0).fill().map((_, i) => (
                    <span key={i} style={styles.rooBadge}>RO</span>
                  ))}
                </div>
                {!p1Active && <div style={styles.activeIndicator}>PLAYING ►</div>}
              </div>
            </>
          );
        })()}
      </div>

      {/* Timer Display */}
      <div style={styles.timerSection}>
        <div style={{ ...styles.timer, color: getTimerColor() }}>
          {formatTime(remaining)}
        </div>
        <div style={styles.timerStatus}>
          {isRunning ? '⏱️ RUNNING' : '⏸️ PAUSED'}
        </div>
        {extensionUsed && <div style={styles.extensionUsed}>Extension Used</div>}
      </div>

      {/* Emergency Controls Toggle */}
      <div style={styles.controlsToggle}>
        <button 
          onClick={() => setShowControls(!showControls)}
          style={styles.toggleButton}
        >
          {showControls ? '🔒 Hide Controls' : '🔓 Show Controls'}
        </button>
      </div>

      {/* Emergency Controls */}
      {showControls && (
        <div style={styles.emergencyControls}>
          <p style={styles.emergencyLabel}>⚠️ Emergency Controls</p>
          <div style={styles.controlsGrid}>
            <button onClick={handleSwitch} style={styles.controlButton}>
              🔄 SWITCH
            </button>
            <button onClick={handleReset} style={styles.controlButton}>
              ↻ RESET
            </button>
            <button 
              onClick={handleExtension}
              disabled={extensionUsed}
              style={{
                ...styles.controlButton,
                ...(extensionUsed && styles.disabledButton)
              }}
            >
              ⏱️ +{clockData.extensionDuration}s
            </button>
            <button 
              onClick={handlePauseResume}
              style={{
                ...styles.controlButton,
                backgroundColor: isRunning ? '#f59e0b' : '#10b981'
              }}
            >
              {isRunning ? '⏸️ PAUSE' : '▶️ START'}
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={styles.footer}>
        <span>Scores update live from CueScore</span>
        <span>•</span>
        <span>🟢 Connected</span>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0d1321',
    color: '#f0f2f5',
    padding: '24px',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  // Scan mode styles
  scanHeader: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  scanTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    fontFamily: "'Outfit', sans-serif",
    marginBottom: '8px'
  },
  scanSubtitle: {
    fontSize: '18px',
    color: '#8b95a8'
  },
  scannerWrapper: {
    width: '100%',
    maxWidth: '400px',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '24px'
  },
  scanHint: {
    color: '#6b7280',
    fontSize: '14px',
    marginBottom: '24px'
  },
  errorBox: {
    backgroundColor: '#ef444420',
    color: '#ef4444',
    padding: '12px 24px',
    borderRadius: '10px',
    marginBottom: '16px'
  },
  codeEntryToggle: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#8b95a8',
    border: '1px solid #2a3555',
    borderRadius: '10px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  // Code entry styles
  codeEntrySection: {
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center'
  },
  codeEntryLabel: {
    color: '#8b95a8',
    marginBottom: '16px',
    fontSize: '16px'
  },
  codeInput: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#1c2640',
    color: 'white',
    border: '2px solid #2a3555',
    borderRadius: '12px',
    fontSize: '24px',
    textAlign: 'center',
    letterSpacing: '4px',
    fontWeight: 'bold',
    marginBottom: '16px'
  },
  codeErrorText: {
    color: '#ef4444',
    fontSize: '14px',
    marginBottom: '16px'
  },
  codeButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  codeSubmitButton: {
    padding: '14px 32px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  codeCancelButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#8b95a8',
    border: '1px solid #2a3555',
    borderRadius: '10px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  // Error/Loading states
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    textAlign: 'center'
  },
  retryButton: {
    padding: '12px 32px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    fontSize: '24px',
    color: '#8b95a8'
  },
  // Display mode styles
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '32px'
  },
  tournamentName: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#8b95a8'
  },
  tableBadge: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600'
  },
  playersSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '32px',
    marginBottom: '40px',
    width: '100%',
    maxWidth: '900px'
  },
  playerCard: {
    flex: 1,
    padding: '24px',
    backgroundColor: '#1c2640',
    borderRadius: '16px',
    textAlign: 'center',
    border: '3px solid transparent',
    transition: 'all 0.3s ease'
  },
  activePlayer: {
    borderColor: '#10b981',
    boxShadow: '0 0 30px rgba(16, 185, 129, 0.3)'
  },
  playerName: {
    fontSize: '32px',
    fontWeight: '700',
    marginBottom: '8px'
  },
  runOuts: {
    display: 'flex',
    gap: '6px',
    justifyContent: 'center',
    marginBottom: '8px',
    minHeight: '28px'
  },
  rooBadge: {
    padding: '4px 8px',
    backgroundColor: '#10b981',
    color: 'white',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '700'
  },
  activeIndicator: {
    color: '#10b981',
    fontSize: '16px',
    fontWeight: '600',
    marginTop: '8px'
  },
  scoreSection: {
    textAlign: 'center'
  },
  score: {
    fontSize: '72px',
    fontWeight: '800',
    fontFamily: "'Outfit', sans-serif"
  },
  scoreLabel: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '4px'
  },
  timerSection: {
    textAlign: 'center',
    marginBottom: '40px'
  },
  timer: {
    fontSize: '120px',
    fontWeight: '800',
    fontFamily: "'Outfit', sans-serif",
    lineHeight: 1
  },
  timerStatus: {
    fontSize: '24px',
    color: '#8b95a8',
    marginTop: '16px'
  },
  extensionUsed: {
    marginTop: '12px',
    padding: '8px 16px',
    backgroundColor: '#f59e0b20',
    color: '#f59e0b',
    borderRadius: '8px',
    fontSize: '16px',
    display: 'inline-block'
  },
  // Emergency controls
  controlsToggle: {
    marginBottom: '16px'
  },
  toggleButton: {
    padding: '10px 24px',
    backgroundColor: '#1c2640',
    color: '#8b95a8',
    border: '1px solid #2a3555',
    borderRadius: '10px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  emergencyControls: {
    backgroundColor: '#1c2640',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '24px',
    width: '100%',
    maxWidth: '500px'
  },
  emergencyLabel: {
    textAlign: 'center',
    color: '#f59e0b',
    marginBottom: '16px',
    fontSize: '14px'
  },
  controlsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px'
  },
  controlButton: {
    padding: '16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  disabledButton: {
    opacity: 0.4,
    cursor: 'not-allowed'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '14px',
    color: '#6b7280',
    marginTop: 'auto'
  }
};

export default AdHocClockDisplay;